import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import _ from 'lodash';

interface ILocaleLink {
  route?: string;
  name: string;
  onClick?: (name: string) => void;
  value?: string;
  index?: number;
}

export interface ILocaleLinksStateProps {
  localeKey: string;
  links: ILocaleLink[];
}

/**
 * Renders a localization string with links in it.
 * Example:
 *  Locale: {"mystring": "This contains a <link name=link1>link a</link> and another <link name=link2>link b</link>."}
 *  Component: <LocaleLinks localeKey={'key'} link={[{name: 'link1', route: '/'}, {name: 'link2', route: '/other'}]} />
 *  Renders: "This contains a <NavLink to='/'>link a</NavLink> and another <NavLink to='/other'>link b</NavLink>."
 */
const LocaleLinks: React.FC<ILocaleLinksStateProps> = ({
  localeKey: key,
  links,
}) => {
  const { t } = useTranslation();

  let text = t(key);
  const delimiter = '$delimiter';

  // iterate each link
  links = links.map((link): ILocaleLink => {
    // define regex that can capture the link in the locale
    const regex = new RegExp(
      `<link\\sname=${link.name}>((?:(?!<).)+)<\\/link>`
    );
    let match = text.match(regex);

    // there should be a match
    if (match) {
      // set the link value to text content of the link in the locale string
      // replace the entire link the locale string with a | character so we can easily split later
      text = text.replace(regex, delimiter);
      return {
        ...link,
        value: _.get(match, 1, ''),
        index: match.index,
      };
    } else {
      console.error(`no match for ${link.name} in "${text}"`);
      return link;
    }
  });

  // attempt to remove any incorrect links
  const fixRegex = new RegExp('<(?:\\/)?\\S+(?:\\sname=(?:\\w|\\d)*)?>', 'gi');
  text = text.replace(fixRegex, '');

  // split all the text pieces based on | character so we can re-assemble with NavLink or span pieces
  const strings = text.split(delimiter);

  return (
    <React.Fragment>
      {_(links)
        .sortBy((q) => q.index)
        .map((link, index) => (
          <React.Fragment key={index}>
            {_.get(strings, index, '')}
            {link.route && link.value ? (
              <NavLink to={link.route as string}>{link.value}</NavLink>
            ) : null}
            {link.onClick && link.value ? (
              <span
                className='link'
                onClick={() => {
                  if (link.onClick) link.onClick(link.name);
                }}
              >
                {link.value}
              </span>
            ) : null}
          </React.Fragment>
        ))
        .value()}
      {_.get(strings, links.length, '')}
    </React.Fragment>
  );
};

export default LocaleLinks;
